// Types
import { ReactNode, useEffect } from "react";

// Libs
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Hooks
import { useOrdersApi } from "@/hooks/useOrdersApi";
import { useOrderFilters } from "@/hooks/useOrderFilters";

// Components
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/select";
import { Input } from "@/components/atoms/input";
import { Button } from "@/components/atoms/button";
import { Textarea } from "@/components/atoms/textarea";
import { DateTimePicker } from "@/components/atoms/date-time-picker";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/atoms/tabs";
import { LocationPicker } from "@/components/LocationPicker";

// Icons
import {
  Loader2,
  Package,
  User,
  MapPin,
  CreditCard,
  AlertCircle,
  Calendar,
  Clock,
  X,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { toastHandler } from "@/lib/ToastHandler";
import { useBranches } from "@/hooks/useBranches";

// Schema de validación con validaciones mejoradas
const createOrderSchema = z.object({
  // Información básica de la orden
  tracking_id: z
    .string()
    .min(1, "ID de seguimiento requerido")
    .max(50, "ID de seguimiento debe tener máximo 50 caracteres"),
  public_id: z
    .number()
    .min(1, "ID público requerido")
    .optional()
    .refine((val) => val !== undefined, {
      message: "ID público es requerido",
    }),
  order_type: z.enum(["delivery", "pickup"], {
    required_error: "Tipo de orden requerido",
  }),
  priority: z.enum(["0", "10", "20"], {
    required_error: "Prioridad es requerida",
    invalid_type_error:
      "Prioridad debe ser 0 (normal), 10 (media/redirección) o 20 (alta/queja)",
  }),
  status: z.enum(
    [
      "pending",
      "accepted",
      "sorted",
      "in_transit",
      "on_site",
      "completed",
      "cancelled",
    ],
    {
      required_error: "Estado requerido",
    },
  ),
  branch_id: z
    .number()
    .min(1, "Sucursal requerida")
    .optional()
    .refine((val) => val !== undefined, {
      message: "Sucursal es requerida",
    }),

  // Información financiera con validaciones mejoradas
  subtotal: z
    .number()
    .min(0.01, "Subtotal debe ser un número positivo mayor a 0")
    .max(999999.99, "Subtotal no puede exceder $999,999.99"),
  total_delivery_charge: z
    .number()
    .min(0, "Cargo de entrega debe ser un número positivo o cero")
    .max(99999.99, "Cargo de entrega no puede exceder $99,999.99"),
  discount: z
    .number()
    .min(0, "Descuento debe ser un número positivo o cero")
    .max(99999.99, "Descuento no puede exceder $99,999.99"),
  tax: z
    .number()
    .min(0, "Impuesto debe ser un número positivo o cero")
    .max(99999.99, "Impuesto no puede exceder $99,999.99"),
  currency: z
    .string()
    .min(1, "Moneda requerida")
    .max(3, "Código de moneda debe tener máximo 3 caracteres"),
  is_paid: z.boolean(),
  paid_with: z.enum(["cash", "card", "transfer", "other"], {
    required_error: "Método de pago requerido",
  }),

  // Información del cliente
  customer_firstName: z
    .string()
    .min(1, "Nombre requerido")
    .max(100, "Nombre debe tener máximo 100 caracteres"),
  customer_firstLastName: z
    .string()
    .min(1, "Apellido requerido")
    .max(100, "Apellido debe tener máximo 100 caracteres"),
  customer_countryCode: z
    .string()
    .min(1, "Código de país requerido")
    .max(5, "Código de país debe tener máximo 5 caracteres"),
  customer_phone: z
    .string()
    .min(1, "Teléfono requerido")
    .max(20, "Teléfono debe tener máximo 20 caracteres"),
  customer_isCellPhone: z.boolean().default(true),

  // Información de dirección
  address_street: z
    .string()
    .min(1, "Calle requerida")
    .max(200, "Calle debe tener máximo 200 caracteres"),
  address_city: z
    .string()
    .min(1, "Ciudad requerida")
    .max(100, "Ciudad debe tener máximo 100 caracteres"),
  address_zipCode: z
    .string()
    .regex(/^\d+$/, "Código postal debe contener solo números")
    .max(10, "Código postal debe tener máximo 10 caracteres"),

  // Información adicional
  comment: z
    .string()
    .min(1, "Comentario es requerido")
    .max(500, "Comentario debe tener máximo 500 caracteres"),
  reception_at: z.string().min(1, "Fecha de recepción requerida"),
  dispatch_date: z.string().min(1, "Fecha de despacho requerida"),
});

type CreateOrderFormData = z.infer<typeof createOrderSchema>;

interface CreateOrderModalProps {
  trigger: ReactNode;
  onOrderCreated?: () => void;
}

// Mapeo de campos a tabs para mostrar errores
const fieldToTabMap: Record<string, string> = {
  tracking_id: "basic",
  public_id: "basic",
  order_type: "basic",
  priority: "basic",
  status: "basic",
  branch_id: "basic",
  reception_at: "basic",
  dispatch_date: "basic",

  customer_firstName: "customer",
  customer_firstLastName: "customer",
  customer_countryCode: "customer",
  customer_phone: "customer",
  customer_isCellPhone: "customer",

  address_fullAddress: "address",
  address_street: "address",
  address_city: "address",
  address_zipCode: "address",
  address_latitude: "address",
  address_longitude: "address",

  subtotal: "payment",
  total_delivery_charge: "payment",
  discount: "payment",
  tax: "payment",
  currency: "payment",
  is_paid: "payment",
  paid_with: "payment",
  comment: "payment",
};

const tabLabels: Record<string, string> = {
  basic: "Básico",
  customer: "Cliente",
  address: "Dirección",
  payment: "Pago",
};

export function CreateOrderModal({
  trigger,
  onOrderCreated,
}: CreateOrderModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const { createManualOrder, loading } = useOrdersApi();
  const { branches, fetchBranches } = useBranches();
  const { isLoadingUser } = useOrderFilters();
  useEffect(() => {
    fetchBranches();
  }, []);
  const [addressData, setAddressData] = useState({
    location_lat: 13.6929,
    location_lon: -89.2182,
    full_address: "",
  });
  const [showAddressErrors, setShowAddressErrors] = useState(false);

  // Branches are auto-fetched and cached by useBranches
  const form = useForm<CreateOrderFormData>({
    resolver: zodResolver(createOrderSchema),
    defaultValues: {
      tracking_id: "",
      public_id: undefined,
      order_type: "delivery",
      priority: "0",
      status: "pending",
      branch_id: undefined,
      subtotal: 0.01,
      total_delivery_charge: 0,
      discount: 0,
      tax: 0,
      currency: "USD",
      is_paid: false,
      paid_with: "cash",
      customer_firstName: "",
      customer_firstLastName: "",
      customer_countryCode: "+502",
      customer_phone: "",
      customer_isCellPhone: true,
      address_street: "",
      address_city: "",
      address_zipCode: "",
      comment: "Orden creada manualmente",
      reception_at: new Date().toISOString().slice(0, 16),
      dispatch_date: new Date(Date.now() + 2 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 16), // 2 hours from now
    },
  });

  // Función para validar addressData
  const validateAddressData = () => {
    const errors: string[] = [];

    if (
      !addressData.full_address ||
      addressData.full_address.trim().length === 0
    ) {
      errors.push("Dirección completa es requerida");
    }

    if (addressData.location_lat === 0 || addressData.location_lon === 0) {
      errors.push("Ubicación en el mapa es requerida");
    }

    if (Math.abs(addressData.location_lat) > 90) {
      errors.push("Latitud debe estar entre -90 y 90");
    }

    if (Math.abs(addressData.location_lon) > 180) {
      errors.push("Longitud debe estar entre -180 y 180");
    }

    return errors;
  };

  const handleSubmit = async (data: CreateOrderFormData) => {
    // Validate all form fields before submission
    const isValid = await form.trigger();

    // Validate addressData separately
    const addressErrors = validateAddressData();

    if (!isValid || addressErrors.length > 0) {
      if (addressErrors.length > 0) {
        // Show toast for address errors and navigate to address tab
        setShowAddressErrors(true);
        setActiveTab("address");
        toastHandler({
          title: "Error en dirección",
          description: addressErrors.join(", "),
          variant: "destructive",
        });
      } else {
        handleFormError(form.formState.errors);
      }
      return;
    }

    try {
      // Get the selected branch to retrieve the organization code
      const selectedBranch = branches.find(
        (branch) => branch.id === data.branch_id!,
      );
      if (!selectedBranch) {
        throw new Error("Sucursal seleccionada no encontrada");
      }

      // Preparar datos para la API
      const orderData = {
        tracking_id: data.tracking_id,
        subtotal: data.subtotal,
        total_delivery_charge: data.total_delivery_charge,
        discount: data.discount,
        tax: data.tax,
        comment: data.comment || "",
        is_paid: data.is_paid,
        currency: data.currency,
        order_type: data.order_type,
        public_id: data.public_id!,
        paid_with: data.paid_with,
        status: data.status,
        reception_at: data.reception_at,
        dispatch_date: data.dispatch_date || undefined,
        duration_ms: 0,
        invoice_lock: false,

        is_external: false,
        priority: data.priority!, // Ya es string
        organization_code: selectedBranch.code, // Add the missing organization_code field
        customer: {
          first_name: data.customer_firstName,
          first_last_name: data.customer_firstLastName,
          full_name: `${data.customer_firstName} ${data.customer_firstLastName}`,
          is_cell_phone: data.customer_isCellPhone,
          country_code: data.customer_countryCode,
          phone: data.customer_phone,
        },
        address: {
          latitude: addressData.location_lat,
          longitude: addressData.location_lon,
          full_address: addressData.full_address,
          street: data.address_street,
          zip_code: data.address_zipCode,
          city: data.address_city,
        },
        driver_id: null,
        branch_id: data.branch_id!,
      };

      await createManualOrder(orderData);

      // Mostrar toast de éxito
      toastHandler({
        title: "Orden creada exitosamente",
        description: "La orden ha sido creada y está lista para ser procesada.",
        variant: "success",
      });

      // Cerrar modal y resetear formulario
      setIsOpen(false);
      form.reset();
      setActiveTab("basic");
      // Reset address data
      setAddressData({
        location_lat: 13.6929,
        location_lon: -89.2182,
        full_address: "",
      });

      // Callback para actualizar la lista de órdenes
      if (onOrderCreated) {
        onOrderCreated();
      }
    } catch (error: any) {
      toastHandler({
        title: "Error al crear la orden",
        description: error.message,
        variant: "destructive",
      });
      console.error("Error creating order:", error);
    }
  };

  const handleFormError = (errors: any) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      // Define el orden de navegación de las tabs
      const tabOrder = ["basic", "customer", "address", "payment"];

      // Encontrar la primera tab con errores siguiendo el orden secuencial
      let firstTabWithError = null;
      for (const tab of tabOrder) {
        const hasErrorInTab = errorFields.some(
          (field) => fieldToTabMap[field] === tab,
        );
        if (hasErrorInTab) {
          firstTabWithError = tab;
          break;
        }
      }

      if (firstTabWithError) {
        // Cambiar a la primera tab que tiene errores en el orden secuencial
        setActiveTab(firstTabWithError);

        // Contar errores por tab para mostrar información más útil
        const errorsByTab: Record<string, number> = {};
        errorFields.forEach((field) => {
          const tab = fieldToTabMap[field];
          if (tab) {
            errorsByTab[tab] = (errorsByTab[tab] || 0) + 1;
          }
        });

        const errorCount = errorsByTab[firstTabWithError];
        const tabLabel = tabLabels[firstTabWithError];

        // Mostrar toast indicando en qué tab está el error
        toastHandler({
          title: "Campos requeridos",
          description: `Complete ${errorCount} campo${errorCount > 1 ? "s" : ""} en la sección "${tabLabel}" para continuar.`,
          variant: "warning",
        });
      } else {
        // Error genérico si no podemos determinar la tab
        toastHandler({
          title: "Formulario incompleto",
          description:
            "Por favor, revise y complete todos los campos requeridos.",
          variant: "warning",
        });
      }
    }
  };

  // Función para validar y avanzar a la siguiente tab
  const validateAndAdvanceTab = async (currentTab: string, nextTab: string) => {
    // Trigger validation for the current tab fields
    const tabFields = Object.keys(fieldToTabMap).filter(
      (field) => fieldToTabMap[field] === currentTab,
    );

    // Trigger validation for the specific fields
    const isValid = await form.trigger(tabFields as any);

    // Additional validation for address tab
    let hasAddressErrors = false;
    if (currentTab === "address") {
      const addressErrors = validateAddressData();
      if (addressErrors.length > 0) {
        setShowAddressErrors(true);
        hasAddressErrors = true;
        toastHandler({
          title: "Error en dirección",
          description: addressErrors.join(", "),
          variant: "warning",
        });
      }
    }

    if (isValid && !hasAddressErrors) {
      // All fields in current tab are valid, advance to next tab
      setActiveTab(nextTab);
    } else {
      // There are validation errors, show toast and let form display field errors
      if (!hasAddressErrors) {
        const errors = form.formState.errors;
        const errorFields = tabFields.filter(
          (field) => errors[field as keyof typeof errors],
        );

        toastHandler({
          title: "Información incompleta",
          description: `Complete ${errorFields.length} campo${errorFields.length > 1 ? "s" : ""} requerido${errorFields.length > 1 ? "s" : ""} antes de continuar.`,
          variant: "warning",
        });
      }
    }
  };

  const tabOrder = ["basic", "customer", "address", "payment"];
  const currentTabIndex = tabOrder.indexOf(activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === tabOrder.length - 1;

  const goToPreviousTab = () => {
    if (!isFirstTab) {
      setActiveTab(tabOrder[currentTabIndex - 1]);
    }
  };

  const goToNextTab = () => {
    if (!isLastTab) {
      validateAndAdvanceTab(activeTab, tabOrder[currentTabIndex + 1]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="w-screen h-screen sm:w-[95vw] sm:h-[95vh] md:h-[90vh] sm:max-w-4xl lg:max-w-6xl sm:rounded-2xl bg-gradient-to-br from-gray-50 to-white border-0 !p-0 overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target && target.closest && target.closest(".pac-container")) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = (e.target as HTMLElement) || null;
          if (
            target &&
            (target as any).closest &&
            (target as any).closest(".pac-container")
          ) {
            e.preventDefault();
          }
        }}
      >
        {/* Mobile Header */}
        <div className="sm:hidden sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Nueva Orden</h2>
                <p className="text-xs text-gray-500">{tabLabels[activeTab]}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Mobile Progress Bar */}
          <div className="px-4 pb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">
                Paso {currentTabIndex + 1} de {tabOrder.length}
              </span>
              <span className="text-xs text-gray-500">
                {Math.round(((currentTabIndex + 1) / tabOrder.length) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{
                  width: `${((currentTabIndex + 1) / tabOrder.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Desktop Header */}
        <DialogHeader className="hidden sm:block sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 p-4 md:p-5 lg:p-6 max-h-[100px]">
          <DialogTitle className="flex items-center gap-3 md:gap-4 text-xl md:text-2xl font-bold text-gray-900">
            <div className="p-2 md:p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl md:rounded-2xl shadow-lg">
              <Package className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white" />
            </div>
            <div>
              <span>Crear Nueva Orden</span>
              <p className="text-xs md:text-sm font-normal text-gray-600 mt-1">
                Complete la información para generar la orden
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className=" max-w-4xl mx-auto sm:px-4">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit, handleFormError)}
                className="space-y-0"
              >
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  {/* Desktop Tabs */}
                  <TabsList className="hidden sm:grid w-full grid-cols-4 bg-white border border-gray-200 mb-4 p-1 lg:px-2 rounded-2xl shadow-sm h-12 md:h-13 lg:h-14">
                    <TabsTrigger
                      value="basic"
                      className="flex items-center gap-2 md:gap-3 text-sm font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl transition-all duration-300 py-2 md:py-2 px-3 md:px-4"
                    >
                      <Package className="w-4 h-4" />
                      <span>Básico</span>
                      {Object.keys(form.formState.errors).some(
                        (field) => fieldToTabMap[field] === "basic",
                      ) && (
                        <AlertCircle className="w-3 h-3 text-red-500 animate-pulse" />
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="customer"
                      className="flex items-center gap-2 md:gap-3 text-sm font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl transition-all duration-300 py-2 md:py-2 px-3 md:px-4"
                    >
                      <User className="w-4 h-4" />
                      <span>Cliente</span>
                      {Object.keys(form.formState.errors).some(
                        (field) => fieldToTabMap[field] === "customer",
                      ) && (
                        <AlertCircle className="w-3 h-3 text-red-500 animate-pulse" />
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="address"
                      className="flex items-center gap-2 md:gap-3 text-sm font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl transition-all duration-300 py-2 md:py-2 px-3 md:px-4"
                    >
                      <MapPin className="w-4 h-4" />
                      <span>Dirección</span>
                      {((showAddressErrors &&
                        Object.keys(form.formState.errors).some(
                          (field) => fieldToTabMap[field] === "address",
                        )) ||
                        (showAddressErrors &&
                          validateAddressData().length > 0)) && (
                        <AlertCircle className="w-3 h-3 text-red-500 animate-pulse" />
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="payment"
                      className="flex items-center gap-2 md:gap-3 text-sm font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl transition-all duration-300 py-2 md:py-2 px-3 md:px-4"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>Pago</span>
                      {Object.keys(form.formState.errors).some(
                        (field) => fieldToTabMap[field] === "payment",
                      ) && (
                        <AlertCircle className="w-3 h-3 text-red-500 animate-pulse" />
                      )}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent
                    value="basic"
                    className="space-y-4 md:space-y-5 lg:space-y-6 bg-white rounded-2xl p-4 md:p-5 lg:p-6 border border-gray-100 shadow-sm"
                  >
                    <div className="space-y-4 md:space-y-5 lg:space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="tracking_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                ID de Seguimiento
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="TRK123456789"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="public_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                ID Público
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  value={
                                    field.value !== undefined ? field.value : ""
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    field.onChange(
                                      value ? Number(value) : undefined,
                                    );
                                  }}
                                  placeholder="100"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                  style={{
                                    MozAppearance: "textfield",
                                    appearance: "textfield",
                                  }}
                                  onWheel={(e) =>
                                    (e.target as HTMLInputElement).blur()
                                  }
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "ArrowUp" ||
                                      e.key === "ArrowDown"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="branch_id"
                          render={({ field }) => (
                            <FormItem className="sm:col-span-2 lg:col-span-1">
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Sucursal
                              </FormLabel>
                              <Select
                                onValueChange={(value) =>
                                  field.onChange(Number(value))
                                }
                                value={
                                  field.value && field.value > 0
                                    ? field.value.toString()
                                    : ""
                                }
                                disabled={isLoadingUser}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium">
                                    <SelectValue
                                      placeholder={
                                        isLoadingUser
                                          ? "Cargando..."
                                          : "Seleccionar sucursal"
                                      }
                                    />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {branches.map((branch) => (
                                    <SelectItem
                                      key={branch.id}
                                      value={branch.id.toString()}
                                    >
                                      {branch.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="order_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Tipo de Orden
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium">
                                    <SelectValue placeholder="Tipo" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="delivery">
                                    🚚 Entrega
                                  </SelectItem>
                                  <SelectItem value="pickup">
                                    📦 Recoger
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="priority"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Prioridad
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium">
                                    <SelectValue placeholder="Prioridad" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="0">🔵 Estándar</SelectItem>
                                  <SelectItem value="10">🟡 Media</SelectItem>
                                  <SelectItem value="20">🔴 Alta</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Estado
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value ?? "pending"}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium">
                                    <SelectValue placeholder="Estado" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="pending">
                                    ⏳ Pendiente
                                  </SelectItem>
                                  <SelectItem value="accepted">
                                    ✅ Aceptado
                                  </SelectItem>
                                  <SelectItem value="sorted">
                                    📋 Ordenado
                                  </SelectItem>
                                  <SelectItem value="in_transit">
                                    🚚 En Tránsito
                                  </SelectItem>
                                  <SelectItem value="on_site">
                                    📍 En Sitio
                                  </SelectItem>
                                  <SelectItem value="completed">
                                    🎯 Completado
                                  </SelectItem>
                                  <SelectItem value="cancelled">
                                    ❌ Cancelado
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="reception_at"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-indigo-600" />
                                Fecha de Recepción
                              </FormLabel>
                              <FormControl>
                                <DateTimePicker
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Seleccionar fecha y hora de recepción"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="dispatch_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-indigo-600" />
                                Fecha de Despacho
                              </FormLabel>
                              <FormControl>
                                <DateTimePicker
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Seleccionar fecha y hora de despacho"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="customer"
                    className="space-y-4 md:space-y-5 lg:space-y-6 bg-white rounded-2xl p-4 md:p-5 lg:p-6 border border-gray-100 shadow-sm"
                  >
                    <div className="space-y-4 md:space-y-5 lg:space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="customer_firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Nombre
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Juan"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="customer_firstLastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Apellido
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Pérez"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="customer_countryCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Código País
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="+502"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="customer_phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Teléfono
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="51130327"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="customer_isCellPhone"
                          render={({ field }) => (
                            <FormItem className="flex flex-col justify-end">
                              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200 h-10 md:h-11 lg:h-12">
                                <input
                                  type="checkbox"
                                  id="is_cell_phone"
                                  checked={field.value}
                                  onChange={field.onChange}
                                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
                                />
                                <label
                                  htmlFor="is_cell_phone"
                                  className="text-sm font-medium text-gray-700"
                                >
                                  📱 Es móvil
                                </label>
                              </div>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="address"
                    className="space-y-4 md:space-y-5 lg:space-y-6 bg-white rounded-2xl p-4 md:p-5 lg:p-6 border border-gray-100 shadow-sm"
                  >
                    <div className="space-y-4 md:space-y-5 lg:space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="address_street"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Calle
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="10 CALLE A"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="address_city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Ciudad
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="MIXCO"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />{" "}
                        <FormField
                          control={form.control}
                          name="address_zipCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Código Postal
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="10001"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>
                      <LocationPicker
                        latitude={addressData?.location_lat || 0}
                        longitude={addressData?.location_lon || 0}
                        onLocationChange={(lat, lng) => {
                          setAddressData((prev) => ({
                            ...prev,
                            location_lat: lat,
                            location_lon: lng,
                          }));
                        }}
                        onAddressChange={(address) => {
                          setAddressData((prev) => ({
                            ...prev,
                            full_address: address,
                          }));
                        }}
                        disabled={false}
                        placeholder={{
                          lat: "13.6929",
                          lng: "-89.2182",
                          address: "Buscar dirección de la sucursal...",
                        }}
                        mapHeight="250px"
                        showAddressSearch={true}
                        addressValue={addressData?.full_address || ""}
                      />
                      {/* Display address validation errors */}
                      {showAddressErrors &&
                        validateAddressData().length > 0 && (
                          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                            {validateAddressData().map((error, index) => (
                              <p
                                key={index}
                                className="text-red-600 text-xs font-medium"
                              >
                                {error}
                              </p>
                            ))}
                          </div>
                        )}
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="payment"
                    className="space-y-4 md:space-y-5 lg:space-y-6 bg-white rounded-2xl p-4 md:p-5 lg:p-6 border border-gray-100 shadow-sm"
                  >
                    <div className="space-y-4 md:space-y-5 lg:space-y-6">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="subtotal"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Subtotal
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  placeholder="0.01"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="total_delivery_charge"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Entrega
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  placeholder="0.00"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="discount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Descuento
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  placeholder="0.00"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="tax"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Impuesto
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  placeholder="0.00"
                                  className="h-10 md:h-11 lg:h-12 text-gray-900 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                        <FormField
                          control={form.control}
                          name="currency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Moneda
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium">
                                    <SelectValue placeholder="Moneda" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="USD">💵 USD</SelectItem>
                                  <SelectItem value="GTQ">🇬🇹 GTQ</SelectItem>
                                  <SelectItem value="EUR">💶 EUR</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="paid_with"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                                Método de Pago
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium">
                                    <SelectValue placeholder="Método" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="cash">
                                    💰 Efectivo
                                  </SelectItem>
                                  <SelectItem value="card">
                                    💳 Tarjeta
                                  </SelectItem>
                                  <SelectItem value="transfer">
                                    🏦 Transfer.
                                  </SelectItem>
                                  <SelectItem value="other">🔄 Otro</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="is_paid"
                          render={({ field }) => (
                            <FormItem className="flex flex-col justify-end">
                              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200 h-10 md:h-11 lg:h-12">
                                <input
                                  type="checkbox"
                                  id="is_paid"
                                  checked={field.value}
                                  onChange={field.onChange}
                                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
                                />
                                <label
                                  htmlFor="is_paid"
                                  className="text-sm font-medium text-gray-700"
                                >
                                  ✅ Pagada
                                </label>
                              </div>
                              <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="comment"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-gray-700 mb-1 md:mb-2 block">
                              Comentarios
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Instrucciones especiales..."
                                rows={2}
                                className="resize-none bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm font-medium placeholder:text-gray-400"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 text-xs font-medium mt-1" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </form>
            </Form>
          </div>
        </div>

        {/* Mobile Navigation Footer */}
        <div className="sm:hidden sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={goToPreviousTab}
              disabled={isFirstTab}
              className="flex-1 h-12 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            {isLastTab ? (
              <Button
                type="button"
                onClick={form.handleSubmit(handleSubmit, handleFormError)}
                disabled={loading || isLoadingUser}
                className="flex-1 h-12 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium shadow-lg disabled:opacity-50"
              >
                {loading || isLoadingUser ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {loading ? "Creando..." : "Cargando..."}
                  </>
                ) : (
                  "Crear Orden"
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={goToNextTab}
                className="flex-1 h-12 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl font-medium shadow-lg"
              >
                Siguiente
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Navigation Footer */}
        <div className="hidden sm:block sticky bottom-0 bg-white border-t border-gray-200 p-4 md:p-5 lg:p-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={goToPreviousTab}
              disabled={isFirstTab}
              className="px-6 py-3 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            <div className="flex items-center gap-2">
              {tabOrder.map((tab, index) => (
                <div
                  key={tab}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index <= currentTabIndex ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                />
              ))}
            </div>

            {isLastTab ? (
              <Button
                type="button"
                onClick={form.handleSubmit(handleSubmit, handleFormError)}
                disabled={loading || isLoadingUser}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium shadow-lg disabled:opacity-50"
              >
                {loading || isLoadingUser ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {loading ? "Creando Orden..." : "Cargando..."}
                  </>
                ) : (
                  "Crear Orden"
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={goToNextTab}
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl font-medium shadow-lg"
              >
                Siguiente
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
